import { ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { Application, CandidateStructuredData } from '@/types';
import {
  resolveCandidateSocialProfiles,
  socialProfileSubmissionLabel,
} from '@/lib/socialProfiles';

type ContactField = {
  label: string;
  value: string;
  href?: string;
};

function ContactFieldGrid({ fields }: { fields: ContactField[] }) {
  if (fields.length === 0) return null;

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {fields.map((field) => (
        <div key={`${field.label}-${field.value}`} className="min-w-0">
          <p className="text-xs text-muted-foreground">{field.label}</p>
          {field.href ? (
            <a
              href={field.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-w-0 max-w-full items-start gap-1 text-sm font-medium break-all text-primary hover:underline"
            >
              <span className="min-w-0 break-all">{field.value}</span>
              <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden="true" />
            </a>
          ) : (
            <p className="text-sm font-medium break-all">{field.value}</p>
          )}
        </div>
      ))}
    </div>
  );
}

function buildFormContactFields(
  app: Application,
  t: (key: string) => string,
): ContactField[] {
  const fields: ContactField[] = [];

  if (app.candidateEmail) {
    fields.push({
      label: t('appDetail.email'),
      value: app.candidateEmail,
      href: `mailto:${app.candidateEmail}`,
    });
  }

  if (app.candidatePhone) {
    fields.push({
      label: t('appDetail.phoneFromForm'),
      value: app.candidatePhone,
      href: `tel:${app.candidatePhone}`,
    });
  }

  for (const profile of resolveCandidateSocialProfiles(app)) {
    const href = profile.url.startsWith('http') ? profile.url : `https://${profile.url}`;
    fields.push({
      label: socialProfileSubmissionLabel(profile, t),
      value: profile.url,
      href,
    });
  }

  return fields;
}

function buildResumeContactFields(
  data: CandidateStructuredData,
  t: (key: string) => string,
): ContactField[] {
  const fields: ContactField[] = [];

  if (data.email) {
    fields.push({ label: t('appDetail.email'), value: data.email, href: `mailto:${data.email}` });
  }
  if (data.phone) {
    fields.push({
      label: t('appDetail.phoneFromResume'),
      value: data.phone,
      href: `tel:${data.phone}`,
    });
  }
  if (data.linkedIn) {
    const href = data.linkedIn.startsWith('http') ? data.linkedIn : `https://${data.linkedIn}`;
    fields.push({ label: 'LinkedIn', value: data.linkedIn, href });
  }
  if (data.github) {
    const href = data.github.startsWith('http') ? data.github : `https://${data.github}`;
    fields.push({ label: 'GitHub', value: data.github, href });
  }
  if (data.portfolio) {
    const href = data.portfolio.startsWith('http') ? data.portfolio : `https://${data.portfolio}`;
    fields.push({ label: t('appDetail.portfolio'), value: data.portfolio, href });
  }

  return fields;
}

export function ApplicationFormContactCard({
  app,
  t,
}: {
  app: Application;
  t: (key: string) => string;
}) {
  const fields = buildFormContactFields(app, t);
  if (fields.length === 0) return null;

  return (
    <Card className="border-border/50">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{t('appDetail.contactFromForm')}</CardTitle>
      </CardHeader>
      <CardContent>
        <ContactFieldGrid fields={fields} />
      </CardContent>
    </Card>
  );
}

export function ApplicationResumeContactCard({
  data,
  t,
}: {
  data: CandidateStructuredData;
  t: (key: string) => string;
}) {
  const fields = buildResumeContactFields(data, t);
  if (fields.length === 0) return null;

  return (
    <Card className="border-border/50">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{t('appDetail.contactFromResume')}</CardTitle>
      </CardHeader>
      <CardContent>
        <ContactFieldGrid fields={fields} />
      </CardContent>
    </Card>
  );
}

export function ApplicationResumeLocationCard({
  data,
  t,
}: {
  data: CandidateStructuredData;
  t: (key: string) => string;
}) {
  if (!data.location?.trim()) return null;

  return (
    <Card className="border-border/50">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{t('appDetail.locationFromResume')}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm font-medium break-words">{data.location}</p>
      </CardContent>
    </Card>
  );
}
